/**
 * Login.jsx
 * Tatalli Cloud — Kurumsal Giriş Sayfası
 * Layout: src/Layouts/Shared/_AuthLayout.jsx
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import AuthLayout from '../../Layouts/Shared/_AuthLayout';
import { redirectToGoogleSSO, saveSession } from '../../Services/authService';
import { useSsoCallback } from '../../hooks/useSsoCallback';

const LEFT_FEATURES = [
  { ikon: 'fa-shield-alt',     key: 'auth.left.feature1' },
  { ikon: 'fa-lock',           key: 'auth.left.feature2' },
  { ikon: 'fa-tachometer-alt', key: 'auth.left.feature3' },
  { ikon: 'fa-globe',          key: 'auth.left.feature4' },
];


export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail]             = useState('');
  const [sifre, setSifre]             = useState('');
  const [sifreGoster, setSifreGoster] = useState(false);
  const [yukleniyor, setYukleniyor]   = useState(false);
  const [hata, setHata]               = useState('');

  useSsoCallback({ onError: setHata, setLoading: setYukleniyor, t });

  const handleGiris = async (e) => {
    if (e) e.preventDefault();
    setYukleniyor(true);
    setHata('');
    axios.defaults.headers.common['X-User-Language'] = t('language', 'en').toUpperCase();
    try {
      const ApiUrl = process.env.REACT_APP_API_URL;
      const response = await axios.post(`${ApiUrl}/access/login/token`, { username: email, password: sifre });
      const data = response.data;
      if (data?.isSucceed) {
        saveSession(data?.payload);
        setTimeout(() => navigate('/portal/changed-language'), 600);
      } else {
        const msg = data?.messages?.[0]?.message;
        setHata(msg || t('api.default.service.response.error'));
        setYukleniyor(false);
      }
    } catch {
      toast.error(t('api.default.service.response.error'));
      setYukleniyor(false);
    }
  };

  return (
    <AuthLayout
      titleKey="pages.login.title"
      leftHeadlineKey="auth.left.headline"
      leftSloganKey="header.slogan"
      leftDescKey="auth.left.description"
      leftFeatures={LEFT_FEATURES}
    >
      <div className="auth-form-konteyner">
        <h2 className="auth-form-baslik">{t('pages.login.header')}</h2>
        <p className="auth-form-aciklama">
          {t('pages.login.description1')}{' '}
          <Link to="#" className="auth-link">
            {t('pages.login.description2')}
          </Link>
        </p>

        {hata && (
          <div className="auth-hata-kutusu">
            <i className="fas fa-exclamation-circle" />
            <span>{hata}</span>
          </div>
        )}

        <form onSubmit={handleGiris}>

          {/* E-posta */}
          <div className="form-grubu">
            <label className="form-etiketi">{t('pages.login.form.label.email')}</label>
            <div className="form-giris-grubu">
              <span className="form-giris-ek">
                <i className="fas fa-envelope" style={{ fontSize: 14, color: '#6b7280' }} />
              </span>
              <input
                type="text" className="form-girisi"
                placeholder={t('pages.login.form.placeholder.email')}
                value={email} onChange={e => setEmail(e.target.value)}
                required autoComplete="email"
              />
            </div>
          </div>

          {/* Şifre */}
          <div className="form-grubu">
            <label className="form-etiketi">{t('pages.login.form.label.password')}</label>
            <div className="form-giris-grubu">
              <span className="form-giris-ek">
                <i className="fas fa-lock" style={{ fontSize: 14, color: '#6b7280' }} />
              </span>
              <input
                type={sifreGoster ? 'text' : 'password'} className="form-girisi"
                placeholder={t('pages.login.form.placeholder.password')}
                value={sifre} onChange={e => setSifre(e.target.value)}
                required autoComplete="current-password"
              />
              <button
                type="button" className="form-giris-ek form-giris-ek-sag"
                onClick={() => setSifreGoster(prev => !prev)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <i className={`fas ${sifreGoster ? 'fa-eye-slash' : 'fa-eye'}`}
                  style={{ fontSize: 14, color: '#6b7280' }} />
              </button>
            </div>
          </div>

          {/* Şifremi unuttum */}
          <div style={{ textAlign: 'right', marginBottom: 24, marginTop: -8 }}>
            <Link to="/reset-password-1" className="auth-link" style={{ fontSize: 13 }}>
              {t('pages.login.form.label.reset-password-1')}
            </Link>
          </div>

          {/* Giriş butonu */}
          <button
            type="submit"
            className="dugme dugme-birincil dugme-buyuk dugme-tam-genislik"
            disabled={yukleniyor}
            style={{ justifyContent: 'center' }}
          >
            {yukleniyor ? (
              <>
                <div className="yuklenici-cember yuklenici-cember-beyaz"
                  style={{ width: 16, height: 16, borderWidth: 2 }} />
                {t('common.please-wait')}
              </>
            ) : (
              <>
                {t('pages.login.form.button.login')}
                <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
              </>
            )}
          </button>

          {/* Divider */}
          <div style={{ textAlign: "center", margin: "16px 0", fontSize: 12, opacity: 0.7 }}>
            
          </div>

          {/* Google ile giriş */}
          <button
            type="button"
            className="dugme dugme-buyuk dugme-tam-genislik"
            style={{
              justifyContent: "center",
              background: "#fff",
              border: "1px solid #ddd",
              color: "#333",
              gap: 8
            }}
            onClick={redirectToGoogleSSO}
          >
            <img
              src="https://developers.google.com/identity/images/g-logo.png"
              alt="Google"
              style={{ width: 18, height: 18 }}
            />
            {t('pages.login.form.button.login-with-google')}
          </button>

        </form>

        <div className="auth-alt-bilgi">
          <p style={{ marginBottom: 4 }}>
            © {new Date().getFullYear()} {t('footer.copyright.company-name')}
          </p>
          <p>
            <a href="#" className="auth-link" style={{ fontSize: 12, color: '#9ca3af' }}>
              {t('auth.privacy')}
            </a>
            {' · '}
            <a href="#" className="auth-link" style={{ fontSize: 12, color: '#9ca3af' }}>
              {t('auth.terms')}
            </a>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}